import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Card, Row, Col, Statistic, Select, Table, Typography, Tooltip, Button, Drawer } from 'antd';
import { EyeOutlined, InfoCircleOutlined } from '@ant-design/icons';
import * as echarts from 'echarts';
import { setRolePreset } from '../../../utils/role';

const { Title, Text } = Typography;

// TODO: 联调时替换为接口数据
const MOCK_KPI = {
    publishedCount: 12,       // 已发布方案数
    selectEntCount: 8,        // 选用企业数
    derivedCount: 23,         // 派生产品数
    authCount: 5800,          // 累计授权采购
    deviceCount: 4600,        // 累计设备数
    avgDevCycle: 11.5,        // 平均开发周期(天)
};

// TODO: 联调时替换为接口数据 —— 方案选用（月粒度，近12个月）
const MOCK_SELECT_MONTHLY = [
    { month: '2025-09', selectEnt: 2, derived: 3 },
    { month: '2025-10', selectEnt: 2, derived: 4 },
    { month: '2025-11', selectEnt: 3, derived: 5 },
    { month: '2025-12', selectEnt: 3, derived: 6 },
    { month: '2026-01', selectEnt: 4, derived: 7 },
    { month: '2026-02', selectEnt: 4, derived: 8 },
    { month: '2026-03', selectEnt: 1, derived: 2 },
    { month: '2026-04', selectEnt: 2, derived: 4 },
    { month: '2026-05', selectEnt: 3, derived: 6 },
    { month: '2026-06', selectEnt: 5, derived: 9 },
    { month: '2026-07', selectEnt: 7, derived: 15 },
    { month: '2026-08', selectEnt: 8, derived: 23 },
];

// TODO: 联调时替换为接口数据 —— 方案选用（日粒度，近30天）
const MOCK_SELECT_DAILY = (() => {
    const arr = [];
    const base = new Date('2026-08-19');
    const entSeed = [1, 1, 2, 1, 1, 2, 1, 2, 1, 1, 2, 1, 2, 1, 1, 2, 1, 1, 2, 2, 1, 1, 2, 1, 2, 1, 1, 2, 1, 1];
    for (let i = 29; i >= 0; i--) {
        const d = new Date(base);
        d.setDate(base.getDate() - i);
        const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const ent = entSeed[29 - i];
        arr.push({ date: dateStr, selectEnt: ent, derived: ent * 2 });
    }
    return arr;
})();

// TODO: 联调时替换为接口数据 —— 设备产出（月粒度，近12个月）
const MOCK_PRODUCE_MONTHLY = [
    { month: '2025-09', newDevice: 150, totalDevice: 150 },
    { month: '2025-10', newDevice: 200, totalDevice: 350 },
    { month: '2025-11', newDevice: 260, totalDevice: 610 },
    { month: '2025-12', newDevice: 320, totalDevice: 930 },
    { month: '2026-01', newDevice: 380, totalDevice: 1310 },
    { month: '2026-02', newDevice: 420, totalDevice: 1730 },
    { month: '2026-03', newDevice: 150, totalDevice: 1880 },
    { month: '2026-04', newDevice: 400, totalDevice: 2280 },
    { month: '2026-05', newDevice: 980, totalDevice: 3260 },
    { month: '2026-06', newDevice: 1600, totalDevice: 4860 },
    { month: '2026-07', newDevice: 2600, totalDevice: 7460 },
    { month: '2026-08', newDevice: 4600, totalDevice: 12060 },
];

// TODO: 联调时替换为接口数据 —— 设备产出（日粒度，近30天）
const MOCK_PRODUCE_DAILY = (() => {
    const arr = [];
    const base = new Date('2026-08-19');
    const newSeed = [30, 40, 50, 60, 45, 70, 55, 80, 65, 50, 90, 75, 60, 85, 70, 100, 80, 65, 95, 110, 90, 75, 105, 120, 95, 80, 110, 130, 100, 90];
    let cum = 0;
    for (let i = 29; i >= 0; i--) {
        const d = new Date(base);
        d.setDate(base.getDate() - i);
        const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const nd = newSeed[29 - i];
        cum += nd;
        arr.push({ date: dateStr, newDevice: nd, totalDevice: cum });
    }
    return arr;
})();

const timeRangeOptions = [
    { value: '7d', label: '近7天' },
    { value: '30d', label: '近30天' },
    { value: 'month', label: '本月' },
    { value: '3m', label: '近3个月' },
    { value: '6m', label: '近6个月' },
    { value: '1y', label: '近一年' },
];

const getSelectTrendData = (range) => {
    if (range === '7d' || range === '30d' || range === 'month') {
        let daily = MOCK_SELECT_DAILY;
        if (range === '7d') daily = daily.slice(-7);
        if (range === 'month') daily = daily.filter((d) => d.date.startsWith('2026-08'));
        return {
            labels: daily.map((d) => d.date.slice(5)),
            selectEnt: daily.map((d) => d.selectEnt),
            derived: daily.map((d) => d.derived),
        };
    }
    const months = { '3m': 3, '6m': 6, '1y': 12 }[range];
    const monthly = MOCK_SELECT_MONTHLY.slice(-months);
    return {
        labels: monthly.map((m) => m.month.slice(2)),
        selectEnt: monthly.map((m) => m.selectEnt),
        derived: monthly.map((m) => m.derived),
    };
};

const getProduceTrendData = (range) => {
    if (range === '7d' || range === '30d' || range === 'month') {
        let daily = MOCK_PRODUCE_DAILY;
        if (range === '7d') daily = daily.slice(-7);
        if (range === 'month') daily = daily.filter((d) => d.date.startsWith('2026-08'));
        return {
            labels: daily.map((d) => d.date.slice(5)),
            newDevice: daily.map((d) => d.newDevice),
            totalDevice: daily.map((d) => d.totalDevice),
        };
    }
    const months = { '3m': 3, '6m': 6, '1y': 12 }[range];
    const monthly = MOCK_PRODUCE_MONTHLY.slice(-months);
    return {
        labels: monthly.map((m) => m.month.slice(2)),
        newDevice: monthly.map((m) => m.newDevice),
        totalDevice: monthly.map((m) => m.totalDevice),
    };
};

// TODO: 联调时替换为接口数据 —— 明星方案 Top5
const MOCK_TOP_PRODUCTS = [
    { key: '1', rank: 1, name: 'test-智能云台摄像机方案', id: 'test-sol-001', category: '摄像机', devCycle: 13, selectEnt: 3, derived: 3, auth: 1500, device: 1200 },
    { key: '2', rank: 2, name: 'test-户外枪机方案', id: 'test-sol-005', category: '摄像机', devCycle: 11, selectEnt: 2, derived: 2, auth: 800, device: 600 },
    { key: '3', rank: 3, name: 'test-4G低功耗电池摄像机方案', id: 'test-sol-002', category: '摄像机', devCycle: 11, selectEnt: 1, derived: 1, auth: 500, device: 320 },
    { key: '4', rank: 4, name: 'test-智能门铃方案', id: 'test-sol-003', category: '摄像机', devCycle: 13, selectEnt: 1, derived: 1, auth: 300, device: 240 },
    { key: '5', rank: 5, name: 'test-NVR网络录像机方案', id: 'test-sol-004', category: '存储类', devCycle: 14, selectEnt: 0, derived: 0, auth: 0, device: 0 },
];

// TODO: 联调时替换为接口数据 —— 选用企业抽屉（按客户维度）
const MOCK_SELECT_ENT_DETAIL = [
    { key: '1', entName: 'test-制造商一有限公司', entId: 'test-ent-mfg-a', derivedCount: 2, deviceCount: 800, coopTime: '2026-05-27' },
    { key: '2', entName: 'test-制造商二科技有限公司', entId: 'test-ent-mfg-b', derivedCount: 1, deviceCount: 400, coopTime: '2026-05-28' },
    { key: '3', entName: 'test-制造商三物联网公司', entId: 'test-ent-mfg-c', derivedCount: 1, deviceCount: 320, coopTime: '2026-05-29' },
];

// TODO: 联调时替换为接口数据 —— 派生产品抽屉（按派生产品维度）
const MOCK_DERIVED_DETAIL = [
    { key: '1', derivedName: 'test-派生-云台高配版', derivedId: 'test-deriv-001', entName: 'test-制造商一有限公司', entId: 'test-ent-mfg-a', deviceCount: 650, coopTime: '2026-05-27' },
    { key: '2', derivedName: 'test-派生-云台标准版', derivedId: 'test-deriv-002', entName: 'test-制造商二科技有限公司', entId: 'test-ent-mfg-b', deviceCount: 400, coopTime: '2026-05-28' },
    { key: '3', derivedName: 'test-派生-云台低配版', derivedId: 'test-deriv-003', entName: 'test-制造商一有限公司', entId: 'test-ent-mfg-a', deviceCount: 150, coopTime: '2026-05-29' },
];

const RANK_BADGE = {
    1: { text: 'Top1', color: '#f59e0b' },
    2: { text: 'Top2', color: '#9ca3af' },
    3: { text: 'Top3', color: '#d97706' },
};

const SolutionDashboard = () => {
    const navigate = useNavigate();
    // 企业视角：本企业 + 合作企业数据
    setRolePreset('enterprise', 'solution');

    const [sortKey, setSortKey] = useState('device');
    const [topN, setTopN] = useState(5);
    const [selectTimeRange, setSelectTimeRange] = useState('7d');
    const [produceTimeRange, setProduceTimeRange] = useState('7d');
    const [selectEntVisible, setSelectEntVisible] = useState(false);
    const [selectEntRecord, setSelectEntRecord] = useState(null);
    const [derivedVisible, setDerivedVisible] = useState(false);
    const [derivedRecord, setDerivedRecord] = useState(null);

    const topNOptions = [
        { value: 5, label: 'Top5' },
        { value: 10, label: 'Top10' },
        { value: 'all', label: '所有方案' },
    ];

    const sortOptions = [
        { value: 'selectEnt', label: '按选用企业数' },
        { value: 'derived', label: '按派生产品数' },
        { value: 'device', label: '按设备数' },
    ];

    const sortedProducts = [...MOCK_TOP_PRODUCTS]
        .sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0))
        .slice(0, topN === 'all' ? undefined : topN)
        .map((r, i) => ({ ...r, rank: i + 1 }));

    const trend1Ref = useRef(null);
    const trend2Ref = useRef(null);
    const trend1Instance = useRef(null);
    const trend2Instance = useRef(null);

    // 图表1：方案选用趋势（新增选用企业数 + 新增派生产品数，依赖时间范围）
    useEffect(() => {
        const { labels, selectEnt, derived } = getSelectTrendData(selectTimeRange);
        if (!trend1Ref.current) return;
        if (!trend1Instance.current) {
            trend1Instance.current = echarts.init(trend1Ref.current);
            trend1Instance.current.setOption({
                legend: { data: ['新增选用企业数', '新增派生产品数'], top: 10, selectedMode: true },
                tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
                grid: { top: 60, left: 60, right: 40, bottom: 40 },
                xAxis: { type: 'category', data: labels, axisLine: { lineStyle: { color: '#999' } } },
                yAxis: { type: 'value', name: '数量', min: 0, minInterval: 1, axisLabel: { formatter: (v) => Math.floor(v) } },
                series: [
                    { name: '新增选用企业数', type: 'bar', data: selectEnt, barWidth: 24, itemStyle: { color: '#5470c6' } },
                    { name: '新增派生产品数', type: 'line', data: derived, smooth: true, lineStyle: { color: '#91cc75', width: 2.5 }, itemStyle: { color: '#91cc75' }, symbol: 'circle', symbolSize: 6 },
                ],
            });
        } else {
            trend1Instance.current.setOption({
                xAxis: { data: labels },
                series: [
                    { name: '新增选用企业数', data: selectEnt },
                    { name: '新增派生产品数', data: derived },
                ],
            });
        }
    }, [selectTimeRange]);

    // 图表2：设备产出趋势（堆叠柱状图：新增设备数 + 设备总数，依赖时间范围）
    useEffect(() => {
        const { labels, newDevice, totalDevice } = getProduceTrendData(produceTimeRange);
        if (!trend2Ref.current) return;
        if (!trend2Instance.current) {
            trend2Instance.current = echarts.init(trend2Ref.current);
            trend2Instance.current.setOption({
                legend: { data: ['新增设备数', '设备总数'], top: 10, selectedMode: true },
                tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
                grid: { top: 60, left: 60, right: 40, bottom: 40 },
                xAxis: { type: 'category', data: labels, axisLine: { lineStyle: { color: '#999' } } },
                yAxis: { type: 'value', name: '数量', min: 0, minInterval: 1, axisLabel: { formatter: (v) => Math.floor(v) } },
                series: [
                    { name: '新增设备数', type: 'bar', data: newDevice, stack: 'total', barWidth: 24, itemStyle: { color: '#5470c6' } },
                    { name: '设备总数', type: 'bar', data: totalDevice, stack: 'total', barWidth: 24, itemStyle: { color: '#91cc75' } },
                ],
            });
        } else {
            trend2Instance.current.setOption({
                xAxis: { data: labels },
                series: [
                    { name: '新增设备数', data: newDevice },
                    { name: '设备总数', data: totalDevice },
                ],
            });
        }
    }, [produceTimeRange]);

    useEffect(() => {
        const handleResize = () => {
            trend1Instance.current?.resize();
            trend2Instance.current?.resize();
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const kpiCards = [
        { title: '已发布方案数', value: MOCK_KPI.publishedCount, suffix: '个', color: '#1890ff', tip: '已发布并审核通过的方案产品数' },
        { title: '平均开发周期', value: MOCK_KPI.avgDevCycle, suffix: '天', color: '#13c2c2', tip: '方案产品从创建到审核通过的平均周期' },
        { title: '我的客户', value: MOCK_KPI.selectEntCount, suffix: '家', color: '#52c41a', tip: '选用本企业方案的制造商企业数（去重）' },
        { title: '派生产品数', value: MOCK_KPI.derivedCount, suffix: '个', color: '#722ed1', tip: '基于本企业方案创建的派生产品总数' },
        { title: '累计授权采购', value: MOCK_KPI.authCount, suffix: '个', color: '#fa8c16', tip: '派生产品累计采购的生产授权数' },
        { title: '累计设备数', value: MOCK_KPI.deviceCount, suffix: '台', color: '#ff4d4f', tip: '派生产品累计产出的激活设备数' },
    ];

    const topColumns = [
        {
            title: '排名', dataIndex: 'rank', width: 80, align: 'center',
            render: (v) => RANK_BADGE[v]
                ? <span style={{ display: 'inline-block', minWidth: 44, padding: '2px 8px', borderRadius: 10, background: RANK_BADGE[v].color, color: '#fff', fontWeight: 600, fontSize: 12 }}>{RANK_BADGE[v].text}</span>
                : <Text strong style={{ color: '#999' }}>{v}</Text>,
        },
        {
            title: '方案产品', key: 'product', width: 240,
            render: (_, r) => (<div>
                <div style={{ fontWeight: 500, color: '#1890ff', cursor: 'pointer' }} onClick={() => navigate(`/product/info?id=${r.id}`)}>{r.name}</div>
                <Text type="secondary" style={{ fontSize: 12 }}>{r.id}</Text>
            </div>),
        },
        { title: '开发周期', dataIndex: 'devCycle', width: 100, align: 'right', render: (v) => <Text strong style={{ color: '#fa8c16' }}>{v}天</Text> },
        {
            title: '选用企业数', dataIndex: 'selectEnt', width: 100, align: 'right',
            render: (v, r) => v > 0
                ? <Button type="link" size="small" style={{ padding: 0, fontWeight: 600 }} onClick={() => { setSelectEntRecord(r); setSelectEntVisible(true); }}>{v}</Button>
                : <span style={{ color: '#ccc' }}>0</span>,
        },
        {
            title: '派生产品数', dataIndex: 'derived', width: 100, align: 'right',
            render: (v, r) => v > 0
                ? <Button type="link" size="small" style={{ padding: 0, fontWeight: 600 }} onClick={() => { setDerivedRecord(r); setDerivedVisible(true); }}>{v}</Button>
                : <span style={{ color: '#ccc' }}>0</span>,
        },
        { title: '设备数', dataIndex: 'device', width: 100, align: 'right', render: (v) => v > 0 ? <Text strong style={{ color: '#fa8c16' }}>{v.toLocaleString()}</Text> : <span style={{ color: '#ccc' }}>0</span> },
    ];

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16 }}><Title level={4}>方案应用情况</Title></div>
            <Alert
                type="warning"
                showIcon
                icon={<EyeOutlined />}
                message="本看板展示贵司方案产品的经营情况，涵盖方案开发、被选用、量产与设备出货全链路，统计范围为本企业及合作企业。"
                style={{ marginBottom: 16, borderRadius: 8 }}
            />

            {/* ═══════════ 核心指标 ═══════════ */}
            <Card title="核心指标" bordered={false} style={{ marginBottom: 16, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <Row gutter={[16, 16]}>
                {kpiCards.map((c) => (
                    <Col span={4} key={c.title}>
                        <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                            <Statistic
                                title={(
                                    <span style={{ fontSize: 13, color: '#606266' }}>
                                        {c.title}
                                        <Tooltip title={c.tip}><InfoCircleOutlined style={{ color: '#999', fontSize: 12, marginLeft: 4, cursor: 'help' }} /></Tooltip>
                                    </span>
                                )}
                                value={c.value}
                                suffix={c.suffix}
                                valueStyle={{ color: c.color, fontWeight: 'bold' }}
                            />
                        </Card>
                    </Col>
                ))}
                </Row>
            </Card>

            {/* ═══════════ 趋势分析 ═══════════ */}
            <Card title="趋势分析" bordered={false} style={{ marginBottom: 16, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <Row gutter={[16, 16]}>
                <Col span={12}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                            <span style={{ fontWeight: 600, fontSize: 15 }}>方案选用趋势</span>
                            <span style={{ width: 1, height: 16, background: '#d9d9d9' }} />
                            <span style={{ color: '#606266', fontSize: 13 }}>时间范围</span>
                            <Select value={selectTimeRange} onChange={setSelectTimeRange} options={timeRangeOptions} style={{ width: 120 }} />
                        </div>
                        <div ref={trend1Ref} style={{ width: '100%', height: 300 }} />
                </Col>
                <Col span={12}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                            <span style={{ fontWeight: 600, fontSize: 15 }}>设备产出趋势</span>
                            <span style={{ width: 1, height: 16, background: '#d9d9d9' }} />
                            <span style={{ color: '#606266', fontSize: 13 }}>时间范围</span>
                            <Select value={produceTimeRange} onChange={setProduceTimeRange} options={timeRangeOptions} style={{ width: 120 }} />
                        </div>
                        <div ref={trend2Ref} style={{ width: '100%', height: 300 }} />
                </Col>
                </Row>
            </Card>

            {/* ═══════════ 明星方案 ═══════════ */}
            <Card bordered={false} style={{ marginBottom: 16, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>明星方案</span>
                    <Select value={topN} onChange={setTopN} options={topNOptions} style={{ width: 120 }} />
                    <span style={{ width: 1, height: 16, background: '#d9d9d9' }} />
                    <span style={{ color: '#606266', fontSize: 13 }}>排名方式</span>
                    <Select value={sortKey} onChange={setSortKey} options={sortOptions} style={{ width: 140 }} />
                </div>
                <Table columns={topColumns} dataSource={sortedProducts} rowKey="key" pagination={false} scroll={{ x: 1000 }} style={{ whiteSpace: 'nowrap' }} />
            </Card>

            {/* ═══════════ 选用企业抽屉 ═══════════ */}
            <Drawer title={`选用企业 - ${selectEntRecord?.name || ''}`} width={720} open={selectEntVisible} onClose={() => setSelectEntVisible(false)} destroyOnClose>
                {selectEntRecord && (
                    <div style={{ background: '#f9f9f9', padding: '12px 16px', borderRadius: 8, marginBottom: 16 }}>
                        <div><Text type="secondary" style={{ display: 'inline-block', width: 88, textAlign: 'right' }}>方案产品：</Text><Text strong>{selectEntRecord.name}</Text></div>
                        <div><Text type="secondary" style={{ display: 'inline-block', width: 88, textAlign: 'right' }}>方案ID：</Text><Text code>{selectEntRecord.id}</Text></div>
                    </div>
                )}
                <Table
                    size="small"
                    dataSource={MOCK_SELECT_ENT_DETAIL}
                    rowKey="key"
                    pagination={false}
                    columns={[
                        { title: '客户信息', key: 'ent', width: 240, render: (_, r) => (<div><div style={{ fontWeight: 500 }}>{r.entName}</div><Text type="secondary" style={{ fontSize: 12 }}>{r.entId}</Text></div>) },
                        { title: '派生产品数', dataIndex: 'derivedCount', width: 100, align: 'right', sorter: (a, b) => a.derivedCount - b.derivedCount, render: (v) => <Text strong>{v}</Text> },
                        { title: '设备数', dataIndex: 'deviceCount', width: 100, align: 'right', sorter: (a, b) => a.deviceCount - b.deviceCount, render: (v) => <Text strong>{v.toLocaleString()}</Text> },
                        { title: '合作时间', dataIndex: 'coopTime', width: 140, defaultSortOrder: 'descend', sorter: (a, b) => a.coopTime.localeCompare(b.coopTime) },
                    ]}
                />
            </Drawer>

            {/* ═══════════ 派生产品抽屉 ═══════════ */}
            <Drawer title={`派生产品 - ${derivedRecord?.name || ''}`} width={720} open={derivedVisible} onClose={() => setDerivedVisible(false)} destroyOnClose>
                {derivedRecord && (
                    <div style={{ background: '#f9f9f9', padding: '12px 16px', borderRadius: 8, marginBottom: 16 }}>
                        <div><Text type="secondary" style={{ display: 'inline-block', width: 88, textAlign: 'right' }}>方案产品：</Text><Text strong>{derivedRecord.name}</Text></div>
                        <div><Text type="secondary" style={{ display: 'inline-block', width: 88, textAlign: 'right' }}>方案ID：</Text><Text code>{derivedRecord.id}</Text></div>
                    </div>
                )}
                <Table
                    size="small"
                    dataSource={MOCK_DERIVED_DETAIL}
                    rowKey="key"
                    pagination={false}
                    columns={[
                        { title: '派生产品', key: 'derived', width: 240, render: (_, r) => (<div><div style={{ fontWeight: 500 }}>{r.derivedName}</div><Text type="secondary" style={{ fontSize: 12 }}>{r.derivedId}</Text></div>) },
                        { title: '客户信息', key: 'ent', width: 240, render: (_, r) => (<div><div style={{ fontWeight: 500 }}>{r.entName}</div><Text type="secondary" style={{ fontSize: 12 }}>{r.entId}</Text></div>) },
                        { title: '设备数', dataIndex: 'deviceCount', width: 100, align: 'right', sorter: (a, b) => a.deviceCount - b.deviceCount, render: (v) => <Text strong>{v.toLocaleString()}</Text> },
                        { title: '合作时间', dataIndex: 'coopTime', width: 140, defaultSortOrder: 'descend', sorter: (a, b) => a.coopTime.localeCompare(b.coopTime) },
                    ]}
                />
            </Drawer>
        </div>
    );
};

export default SolutionDashboard;
